import { BlogPost } from '../../blog/types';

const post: BlogPost = {
  id: 'market-shift',
  title: 'The Market Has Already Changed',
  excerpt:
    "While you wondered when AI might matter, companies running predator-mode operations already rewrote the rules. They're not testing. They're compounding.",
  category: 'Insights',
  date: '2024-08-15',
  readTime: '8 min read',
  imageUrl: '/images/blog/zb-talk.png',
  author: {
    name: 'Zac Brooks',
    imageUrl: '/images/blog/zac-brooks.jpg',
  },
  slug: 'market-shift',
  featured: true,
  content: [
    {
      type: 'heading2',
      content: 'Three Company Archetypes',
      description: 'You can tell who survives by how they treat compounding speed.',
      id: 'three-types'
    },
    {
      type: 'heading3',
      content: 'Type 1: The Walking Dead (≈70%)',
      id: 'walking-dead'
    },
    {
      type: 'markdown',
      content: `Still running the 2019 playbook. Same stack, same hiring model, same weekly status meetings.

**Reality**
- Bleeding market share every quarter
- Hiring freeze disguised as "focus"
- Board keeps asking for the AI memo
- Culture allergic to experimentation

**Trajectory**
- Acquired for cost savings or just left behind.`
    },
    {
      type: 'heading3',
      content: 'Type 2: The Cosplayers (≈25%)',
      id: 'cosplayers'
    },
    {
      type: 'markdown',
      content: `Press releases every quarter. ChatGPT Enterprise seats for everyone. AI Center of Excellence launching workshops where nothing ships.

**Reality**
- Millions spent on tooling for single-digit gains
- Innovation theatre masking stagnation
- "Pilots" never leave notion docs
- Velocity capped by legacy process

**Trajectory**
- Slow fade into irrelevance.`
    },
    {
      type: 'heading3',
      content: 'Type 3: The Predators (≈5%)',
      id: 'new-predators'
    },
    {
      type: 'markdown',
      content: `Rebuilt their operating system. Every workflow instrumented, every decision augmented.

**Reality**
- Compounding velocity: 10x faster, cheaper, smarter
- Tiny teams with outsized surface area
- Talent magnet because builders want leverage
- Strategy adjusts weekly because execution enables it

**Trajectory**
- They write the rules everyone else obeys.`
    },
    {
      type: 'impactStatement',
      content:
        'Predators look like five-person side projects until you notice they shipped three full product lines in 90 days.'
    },
    {
      type: 'divider'
    },
    {
      type: 'heading2',
      content: 'Speed Compounds Like Capital',
      description: 'The gap doubles faster than most boards can schedule a retreat.',
      id: 'why-speed'
    },
    {
      type: 'markdown',
      content: `Operational velocity compounds like interest. The best teams double throughput with the same headcount every 12–18 months.

**Year 0**: Predator team is 10x faster than incumbents
**Year 1**: 20x
**Year 2**: 40x
**Year 3**: 80x

Velocity isn't a vanity metric. It determines who gets to learn, iterate, and set the next constraint. If you're compounding and they're linear, they never catch you.`
    },
    {
      type: 'paragraph',
      content:
        'Linear planners assume there will be time to “do it right later.” Compounding teams turn later into never.'
    },
    {
      type: 'divider'
    },
    {
      type: 'heading2',
      content: 'Talent Moves Toward Leverage',
      description: 'People who can build the future refuse to move backward.',
      id: 'talent-exodus'
    },
    {
      type: 'twoColumnProblemOverview',
      content: {
        problem: {
          title: "WHO'S LEAVING",
          description:
            '• Senior engineers bored of patching legacy stacks\n• Analysts stuck cleaning spreadsheets\n• Designers whose work dies in slide decks\n• Operators forced to ask permission for everything'
        },
        background: {
          title: "WHERE THEY'RE LANDING",
          description:
            '• Teams that deploy weekly\n• Orgs measuring outcomes, not hours\n• Places where one person equals a squad\n• Companies building net-new capability'
        }
      }
    },
    {
      type: 'paragraph',
      content:
        "The loop is vicious: no leverage, no talent; no talent, no leverage. Predator teams broke the loop by giving builders an unfair arena."
    },
    {
      type: 'divider'
    },
    {
      type: 'heading2',
      content: 'Why Winners Look Invisible',
      description: 'Predator-mode compounding hides in plain sight.',
      id: 'invisible-winners'
    },
    {
      type: 'paragraph',
      content:
        'From the outside, a team moving 5x faster barely registers. They just look “well-run.” At 20x, you still miss it—until a market flips in what looks like a single quarter. That overnight success was two years of compounding speed you never measured.'
    },
    {
      type: 'impactStatement',
      content:
        'By the time the predators are visible, the race is already over. Velocity was the tell—you just weren’t tracking it.'
    },
    {
      type: 'divider'
    },
    {
      type: 'heading2',
      content: 'The Predator Mindset for Agencies',
      description: 'Breaking the iron triangle is a discipline, not a slogan.',
      id: 'predator-mindset'
    },
    {
      type: 'paragraph',
      content:
        "For two years we've applied this mindset inside our own software agency. To move like a predator, you treat speed, quality, and cost as outputs of the same system—not trade-offs."
    },
    {
      type: 'keyBulletList',
      content: {
        heading: 'How We Break the Triangle',
        description: 'Velocity is engineered, not wished for.',
        points: [
          {
            icon: 'schedule',
            title: 'Parallelized Development',
            text: 'Multi-agent build pipelines let us ship experiments and production code in the same sprint without trashing quality.'
          },
          {
            icon: 'verified',
            title: 'Embedded Verification',
            text: 'Every deliverable carries its own success criteria and automated checks. QA happens in-line, not at the end.'
          },
          {
            icon: 'insights',
            title: 'Real-Time Instrumentation',
            text: 'Each launch feeds usage analytics back to the team within hours, so we iterate based on signal, not hunches.'
          },
          {
            icon: 'groups',
            title: 'Tiny Cross-Functional Pods',
            text: 'Pods of three to five own outcomes end-to-end. No handoff tax, no waiting, no committees.'
          }
        ]
      }
    },
    {
      type: 'impactStatement',
      content:
        "Predator-mode agencies don't pitch velocity—they operate it. The work proves the model."
    },
    {
      type: 'divider'
    },
    {
      type: 'heading2',
      content: 'Choose Your Operating System',
      description: 'Addition vs. multiplication is a cultural decision.',
      id: 'operating-system'
    },
    {
      type: 'paragraph',
      content:
        "You can manage for predictability and watch faster teams lap you, or manage for compounding speed and redefine the category. Neutral ground doesn't exist."
    },
    {
      type: 'paragraph',
      content:
        'Most companies stay linear because linear feels safe. Predators keep compounding because they build systems that reward forward motion.'
    },
    {
      type: 'impactStatement',
      content:
        'Either you engineer multiplication or you become someone else’s case study.'
    }
  ]
};

export default post;
